// ─── Workshop Truck Availability Tracker ───
// useTrucks – Fetches truck list with dynamic filter support
// Re-fetches automatically when filters change
// IASL Technical Academy / Lafarge Driving Institute, Nigeria

import { useState, useEffect, useCallback, useRef } from 'react'
import type { Truck } from '@/types'
import { api } from '@/utils/api'

export interface TruckFilters {
  client?: string
  status?: string
  in_workshop?: string // 'true' | 'false' | undefined
}

interface ApiTrucksResponse {
  success: boolean
  data: Truck[]
  count: number
}

interface UseTrucksReturn {
  trucks: Truck[]
  total: number
  isLoading: boolean
  error: string | null
  filters: TruckFilters
  setFilters: (f: TruckFilters) => void
  refresh: () => void
}

export function useTrucks(initialFilters: TruckFilters = {}): UseTrucksReturn {
  const [trucks, setTrucks] = useState<Truck[]>([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<TruckFilters>(initialFilters)

  // Track if component is mounted to avoid state updates after unmount
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  const fetchData = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      // Build query string from active filters
      const params = new URLSearchParams()
      if (filters.client)       params.set('client', filters.client)
      if (filters.status)       params.set('status', filters.status)
      if (filters.in_workshop)  params.set('in_workshop', filters.in_workshop)

      const qs = params.toString()
      const path = `/api/trucks${qs ? `?${qs}` : ''}`

      const result = await api.get<ApiTrucksResponse>(path)

      if (mountedRef.current) {
        setTrucks(result.data ?? [])
        setTotal(result.count ?? 0)
      }
    } catch (err) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to load trucks'
        setError(msg)
      }
    } finally {
      if (mountedRef.current) {
        setIsLoading(false)
      }
    }
  }, [filters.client, filters.status, filters.in_workshop])

  // Auto-fetch when filters change
  useEffect(() => {
    fetchData()
  }, [fetchData])

  return {
    trucks,
    total,
    isLoading,
    error,
    filters,
    setFilters,
    refresh: fetchData,
  }
}
